/** @jsx jsx */
import { jsx } from "theme-ui"
import { useState } from "react"
import axios from "axios"
import qs from "query-string"
import { Formik } from "formik"
import get from "lodash/get"
import TextInput from "@components/form/TextInput"
import TextArea from "@components/form/TextArea"
import { rem } from "@src/theme"
import Button from "@components/Button"
import * as Yup from "yup"
import Checkbox from "@components/form/Checkbox"
import CalendarInput from "@components/form/CalendarInput"
import SelectInput from "@components/form/SelectInput"
import "react-dates/initialize"
import "react-dates/lib/css/_datepicker.css"
import CheckboxGroup from "@components/form/CheckboxGroup"
import FieldSpacer from "@components/form/FieldSpacer"

const STATE_OPTIONS = [
  { label: "ACT", value: "ACT" },
  { label: "New South Wales", value: "NSW" },
  { label: "Northern Territory", value: "NT" },
  { label: "Queensland", value: "QLD" },
  { label: "South Australia", value: "SA" },
  { label: "Tasmania", value: "TAS" },
  { label: "Victoria", value: "VIC" },
  { label: "Western Australia", value: "WA" },
]

const validationSchema = Yup.object().shape({
  name: Yup.string().required("This is required"),
  organisation: Yup.string(),
  state: Yup.string().required("This is required"),
  email: Yup.string()
    .email("Must be a valid email")
    .required("This is required"),
  phone: Yup.string().required("This is required"),
  workshops: Yup.array()
    .of(Yup.string()),
  age_range_participants: Yup.string().required("This is required"),
  number_of_participants: Yup.number().typeError("This should be a number"),
})

const initialFormState = {
  name: "",
  organisation: "",
  state: "VIC",
  email: "",
  phone: "",
  workshops: [],
  age_range_participants: "",
  number_of_participants: "",
  date: "",
  previous_booking: [],
  delivery_method: [],
  subject: '',
  referrerName: "",
  otherInformation: "",
  opt_in_subscribe: [],
}

export default ({ formName = "Workshop bookings", workshopOptions }) => {
  const [submissionError, setSubmissionError] = useState(null)
  const [success, setSuccess] = useState(null)

  const getError = (name, errors, touched) =>
    get(touched, name) && get(errors, name) ? get(errors, name) : ""

  const [calendarFocus, setCalendarFocus] = useState(false)

  const onCalendarFocusChange = e => {
    setCalendarFocus(e.focused)
  }

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialFormState}
      onSubmit={(values, actions) => {
        const formData = {}
        Object.keys(values).forEach(key => {



          if (key === "workshops") {
            formData[key] = values[key].join(", ")
          } else if (key === "opt_in_subscribe") {
            if (values.opt_in_subscribe.length > 0) {
              formData[key] = "subscribed"
            } else {
              formData[key] = "unsubscribed"
            }
          } else if (key === "previous_booking") {
            if (values.previous_booking.length > 0) {
              formData[key] = "yes"
            } else {
              formData[key] = "no"
            }
          } else if (key === 'subject') {
            if (formData['organisation']) {
              formData['subject'] = `Minus18 workshop enquiry from ${formData['organisation']}`
            }
          } else {
            formData[key] = values[key]
          }


        })
        formData["form-name"] = formName
        const dt = new Date()


        const mailchimpLambdaOptions = {
          url: "/.netlify/functions/workshopBookings",
          method: "post",
          data: {
            first_name: formData.name,
            email_address: formData.email,
            phone: formData.phone,
            state: formData.state,
            opted_in: formData.opt_in_subscribe,
            organisation: formData.organisation,
            tags: [`Workshops ${dt.getFullYear()}`],
          },
        }

        axios({
          url: "/learn/enquiry-form",
          method: "post",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          data: qs.stringify(formData),
        })
          .then(response => {
            if (!response) {
              throw new Error()
            }
            if (submissionError || success) {
              setSubmissionError(null)
              setSuccess(null)
            }
            setSuccess("We'll be in touch!")
            actions.resetForm(initialFormState)

            axios(mailchimpLambdaOptions)
              .then(response => {
                if (!response) {
                  throw new Error()
                }
                if (submissionError || success) {
                  setSubmissionError(null)
                  setSuccess(null)
                }
                setSuccess("We'll be in touch!")
              })
              .catch(err => {
                console.log(err)
              })
          })
          .catch(err => {
            console.log(err)
            setSubmissionError(
              "There was a problem handling your submission, please contact us via email at info@minus18.org.au or if its urgent, call us on 0478 098 540."
            )
          })
      }}
    >
      {({
        handleBlur,
        handleChange,
        submitForm,
        values,
        errors,
        touched,
        setFieldValue,
      }) => (
        <form
          name={formName}
          method="post"
          data-netlify="true"
          onSubmit={e => {
            e.preventDefault()
            submitForm()
          }}
        >
          <input type="hidden" name="form-name" value={formName} />
          <input type="hidden" name="subject" value={values.subject} />

          <FieldSpacer>
            <TextInput
              value={values.name}
              label="Name"
              name="name"
              onChange={handleChange}
              error={getError("name", errors, touched)}
              onBlur={handleBlur}
              required
            />
            <TextInput
              value={values.organisation}
              label="Organisation"
              name="organisation"
              onChange={handleChange}
              error={getError("organisation", errors, touched)}
              onBlur={handleBlur}
              required
            />
          </FieldSpacer>

          <FieldSpacer>
            <TextInput
              value={values.email}
              label="Email"
              name="email"
              onChange={handleChange}
              error={getError("email", errors, touched)}
              onBlur={handleBlur}
              required
            />

            <TextInput
              value={values.phone}
              label="Phone"
              name="phone"
              onChange={handleChange}
              error={getError("phone", errors, touched)}
              onBlur={handleBlur}
              required
            />
          </FieldSpacer>

          <FieldSpacer>
            <SelectInput
              name="state"
              value={values.state}
              onChange={handleChange}
              label="State"
              onBlur={handleBlur}
              error={false}
              options={STATE_OPTIONS}
              required
            />
          </FieldSpacer>

          <FieldSpacer>
            <div
              sx={{
                '.field-workplace, .field-school': {
                  'label.field-label span ': {
                    color: 'purple'
                  }
                }
              }}
            >
              <label
                className="field-label"
                sx={{
                  fontWeight: 900,
                  marginBottom: rem(20),
                  marginTop: rem(40),
                  display: 'block'
                }}
              >Which workshop are you interested in?

              </label>
              <CheckboxGroup
                name="workshops"
                options={workshopOptions.filter(opt => opt.value.indexOf('WORKPLACE') !== -1)}
                currentValue={values.workshops}
                setFieldValue={setFieldValue}
                error={getError("workshops", errors, touched)}
                label="Workplaces"
                wrapperClass='field-workplace'
              />

              <hr sx={{
                my: rem(30),
                border: 'none',
                height: '1px',
                bg: '#6a509563'
              }} />
              <CheckboxGroup
                name="workshops"
                options={workshopOptions.filter(opt => opt.value.indexOf('SCHOOL') !== -1)}
                currentValue={values.workshops}
                setFieldValue={setFieldValue}
                error={getError("workshops", errors, touched)}
                label="Schools"
                wrapperClass='field-school'
              />

              <hr sx={{
                my: rem(30),
                border: 'none',
                height: '1px',
                bg: '#6a509563'
              }} />
            </div>
          </FieldSpacer>

          <FieldSpacer>
            <TextInput
              value={values.age_range_participants}
              label="What is the age range of participants?"
              name="age_range_participants"
              onChange={handleChange}
              error={getError("age_range_participants", errors, touched)}
              onBlur={handleBlur}
              required
            />

            <TextInput
              value={values.number_of_participants}
              label="How many people in the workshop/s?"
              name="number_of_participants"
              onChange={handleChange}
              error={getError("number_of_participants", errors, touched)}
              onBlur={handleBlur}
            />
          </FieldSpacer>

          <FieldSpacer>
            <CalendarInput
              value={values.date}
              onFocusChange={onCalendarFocusChange}
              focused={calendarFocus}
              onChange={e => setFieldValue("date", e)}
              label="Date"
              name="Date"
            />
          </FieldSpacer>
          <FieldSpacer>
            <CheckboxGroup
              name="delivery_method"
              options={[
                { label: "In-person", value: "In-person" },
                { label: "Digital", value: "Digital" },
              ]}
              currentValue={values.delivery_method}
              setFieldValue={setFieldValue}
              error={getError("delivery_method", errors, touched)}
              label="What is your preferred delivery method?"
            />
          </FieldSpacer>
          <FieldSpacer>
            <CheckboxGroup
              name="previous_booking"
              options={[{ label: "Yes", value: "Yes" }]}
              currentValue={values.previous_booking}
              setFieldValue={setFieldValue}
              error={getError("previous_booking", errors, touched)}
              label="Have you booked previously?"
            />
          </FieldSpacer>

          <FieldSpacer>
            <TextInput
              value={values.referrerName}
              label="How did you hear about us?"
              name="referrerName"
              onChange={handleChange}
              error={getError("referrerName", errors, touched)}
              onBlur={handleBlur}
            />
          </FieldSpacer>

          <FieldSpacer>
            <TextArea
              value={values.otherInformation}
              label="Anything else we should know?"
              name="otherInformation"
              onChange={handleChange}
              error={getError("otherInformation", errors, touched)}
              onBlur={handleBlur}
            />
          </FieldSpacer>

          <FieldSpacer>
            <label
              sx={{
                display: "flex",
              }}
            >
              <Checkbox
                name="opt_in_subscribe"
                value={true}
                setFieldValue={setFieldValue}
                fieldValue={values.opt_in_subscribe}
              />
              <span
                sx={{
                  transform: "translateY(3px)",
                }}
              >
                Keep me up to date with news and updates
              </span>
            </label>
          </FieldSpacer>

          <div>
            <Button type="submit">Submit enquiry</Button>
          </div>

          {submissionError && (
            <p sx={{ color: "red", maxWidth: rem(500) }}>{submissionError}</p>
          )}
          {success && (
            <p sx={{ color: "green", maxWidth: rem(500) }}>{success}</p>
          )}
        </form>
      )}
    </Formik>
  )
}
